// frontend/src/pages/dashboards/FilterDialogs.jsx
// Phase 4 step 14b-2: the dialogs behind the filter bar in edit mode.
//   FilterEditorDialog  add or change one filter: its name, what it narrows
//                       (a date, a list of values, a text search), which
//                       field, and what it starts with
//   TileFiltersDialog   for one chart tile: which field each filter narrows
//                       on it, or that a filter leaves it alone
// Radix dialogs, like TileDialogs: focus stays inside, Escape closes.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'radix-ui';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import {
  FILTER_KINDS, KIND_LABELS, MAX_LABEL, bindingFor, cleanLabel, defaultChoices, definitionProblem, fieldsForKind, newFilterId,
} from './filterState';

const CONTENT = 'fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[22px] bg-[var(--ui-panel)] p-6 text-[var(--ui-text)] shadow-[0_24px_60px_rgba(0,0,0,0.6)] ring-1 ring-white/5 focus:outline-none';
const FIELD = 'w-full rounded-xl bg-[var(--ui-inset)] px-3 py-2 text-[13px] text-[var(--ui-text)] placeholder:text-[var(--ui-text-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30';
const LABEL = 'mb-1.5 block text-xs font-medium text-[var(--ui-text-muted)]';

function Shell({ open, onClose, children, width = 'max-w-md', initialFocus }) {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          className={`${CONTENT} ${width}`}
          onOpenAutoFocus={initialFocus ? (e) => { e.preventDefault(); if (initialFocus.current) initialFocus.current.focus(); } : undefined}
        >
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Loading() {
  return (
    <p className="flex items-center gap-2 py-2 text-xs text-[var(--ui-text-muted)]" role="status">
      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Loading fields
    </p>
  );
}

const KIND_HELP = {
  date: 'A period or a range of days. Each chart uses its own date field unless you pick one.',
  values: 'Pick any of the values a field holds, such as departments or statuses.',
  text: 'Keep the rows whose field contains what is typed.',
};

/**
 * request  { def: the filter to change, or null for a new one, current: the bar's value for it }, or null (closed)
 * fields   { status: 'loading' | 'ready' | 'error', list: [{ key, display_name, data_type, datasets }], datasets: n }
 * others   the dashboard's other definitions (a name must be unique)
 * onSave(def), onClose()
 */
export function FilterEditorDialog({ request, fields, others = [], onSave, onClose }) {
  const nameRef = useRef(null);
  const [form, setForm] = useState({ label: '', kind: 'date', field: null, choice: 'none' });
  const original = request && request.def;

  useEffect(() => {
    if (!request) return;
    const d = request.def;
    setForm(d ? { label: d.label, kind: d.kind, field: d.field ?? null, choice: d.default != null ? 'keep' : 'none' }
      : { label: '', kind: 'date', field: null, choice: 'none' });
  }, [request]);

  // What the default can be: only what fits the kind it had (a changed kind starts from nothing).
  const sameKind = Boolean(original && original.kind === form.kind);
  const choices = useMemo(() => defaultChoices(
    { kind: form.kind, default: sameKind ? original.default : null },
    sameKind ? request && request.current : null,
  ), [form.kind, sameKind, original, request]);
  const choice = choices.find((c) => c.id === form.choice) || choices[0];

  const def = { id: original ? original.id : '', label: form.label, kind: form.kind, field: form.field, default: choice.value };
  const problem = definitionProblem(def, others);
  const options = fieldsForKind(form.kind, fields.list);
  const total = fields.datasets || 0;

  const save = () => {
    if (problem) return;
    onSave({ ...def, id: original ? original.id : newFilterId(others), label: cleanLabel(form.label) });
  };

  return (
    <Shell open={Boolean(request)} onClose={onClose} width="max-w-lg" initialFocus={nameRef}>
      <Dialog.Title className="text-base font-semibold">{original ? 'Edit filter' : 'Add a filter'}</Dialog.Title>
      <Dialog.Description className="mt-1 text-xs text-[var(--ui-text-muted)]">
        A filter narrows every chart on the dashboard that has its field. Viewers change its value; what you set here is saved with the dashboard.
      </Dialog.Description>
      <form className="mt-4 flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <div>
          <label className={LABEL} htmlFor="filter-name">Name</label>
          <input id="filter-name" ref={nameRef} type="text" value={form.label} maxLength={MAX_LABEL} placeholder="Period, Department, …"
            onChange={(e) => setForm({ ...form, label: e.target.value })} className={FIELD} autoComplete="off" />
        </div>

        <fieldset>
          <legend className={LABEL}>Narrows by</legend>
          <div className="grid grid-cols-3 gap-1.5" role="radiogroup" aria-label="Narrows by">
            {FILTER_KINDS.map((k) => (
              <button key={k} type="button" role="radio" aria-checked={form.kind === k}
                onClick={() => setForm({ ...form, kind: k, field: null, choice: 'none' })}
                className={`rounded-xl px-3 py-2 text-[13px] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ${form.kind === k ? 'bg-white/[0.14] font-medium text-[var(--ui-text)]' : 'bg-[var(--ui-inset)] text-[var(--ui-text-muted)] hover:text-[var(--ui-text)]'}`}>
                {KIND_LABELS[k]}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-[var(--ui-text-muted)]">{KIND_HELP[form.kind]}</p>
        </fieldset>

        <div>
          <label className={LABEL} htmlFor="filter-field">Field</label>
          {fields.status === 'loading' ? <Loading /> : (
            <select id="filter-field" className={FIELD} value={form.field ?? ''}
              onChange={(e) => setForm({ ...form, field: e.target.value || null })}>
              <option value="">{form.kind === 'date' ? "Each chart's own date field" : 'Choose a field'}</option>
              {options.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.display_name}{total > 1 ? ` · in ${f.datasets} of ${total} datasets` : ''}
                </option>
              ))}
              {form.field && !options.some((f) => f.key === form.field) && <option value={form.field}>{form.field}</option>}
            </select>
          )}
          {fields.status === 'error' && <p className="mt-1 text-xs text-red-400">The fields couldn't be loaded. You can still keep the field already chosen.</p>}
          {fields.status === 'ready' && !options.length && form.kind !== 'date' && (
            <p className="mt-1 text-xs text-[var(--ui-text-muted)]">No chart on this dashboard has a field of this kind.</p>
          )}
        </div>

        <div>
          <label className={LABEL} htmlFor="filter-default">Starts with</label>
          <select id="filter-default" className={FIELD} value={choice.id} onChange={(e) => setForm({ ...form, choice: e.target.value })}>
            {choices.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>

        {problem && form.label.trim() && <p className="text-xs text-red-400" role="alert">{problem}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" disabled={Boolean(problem)}>{original ? 'Done' : 'Add filter'}</Button>
        </div>
      </form>
    </Shell>
  );
}

const OWN = '__own';
const NONE = '__none';

/**
 * request  { tile, fields: { status, list: the tile's dataset's fields } }, or null (closed)
 * defs     the dashboard's filter definitions
 * onSave(tileKey, { [filterId]: undefined | null | key }), onClose()
 */
export function TileFiltersDialog({ request, defs, onSave, onClose }) {
  const [choice, setChoice] = useState({});
  const tile = request && request.tile;

  useEffect(() => {
    if (!tile) return;
    const next = {};
    for (const d of defs) {
      const b = bindingFor(tile, d.id);
      next[d.id] = b === undefined ? OWN : b === null ? NONE : b;
    }
    setChoice(next);
  }, [tile, defs]);

  if (!request) return <Shell open={false} onClose={onClose}>{null}</Shell>;
  const fields = request.fields || { status: 'loading', list: [] };
  const byKey = new Map((fields.list || []).map((f) => [f.key, f]));
  const name = (key) => (byKey.get(key) ? byKey.get(key).display_name || key : key);

  const save = () => {
    const out = {};
    for (const d of defs) {
      const c = choice[d.id];
      out[d.id] = c === OWN || c === undefined ? undefined : c === NONE ? null : c;
    }
    onSave(tile.key, out);
  };

  const ownLabel = (d) => {
    if (d.kind === 'date' && !d.field) return "This chart's own date field";
    if (fields.status === 'ready' && !byKey.has(d.field)) return `${d.field} (this chart's data has no such field)`;
    return name(d.field);
  };

  return (
    <Shell open onClose={onClose} width="max-w-lg">
      <Dialog.Title className="text-base font-semibold">Filters on this tile</Dialog.Title>
      <Dialog.Description className="mt-1 text-xs text-[var(--ui-text-muted)]">
        Choose which field of this chart each filter narrows, or leave the tile out of a filter.
      </Dialog.Description>
      <form className="mt-4 flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); save(); }}>
        {fields.status === 'loading' && <Loading />}
        {defs.map((d) => {
          const others = fieldsForKind(d.kind, fields.list).filter((f) => f.key !== d.field);
          const value = choice[d.id] ?? OWN;
          return (
            <div key={d.id}>
              <label className={LABEL} htmlFor={`bind-${d.id}`}>{d.label}</label>
              <select id={`bind-${d.id}`} className={FIELD} value={value} onChange={(e) => setChoice({ ...choice, [d.id]: e.target.value })}>
                <option value={OWN}>{ownLabel(d)}</option>
                {others.map((f) => <option key={f.key} value={f.key}>{f.display_name || f.key}</option>)}
                {value !== OWN && value !== NONE && !others.some((f) => f.key === value) && <option value={value}>{value}</option>}
                <option value={NONE}>Not filtered by {d.label}</option>
              </select>
            </div>
          );
        })}
        {fields.status === 'error' && <p className="text-xs text-red-400">This chart's fields couldn't be loaded; only its current settings are listed.</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm">Done</Button>
        </div>
      </form>
    </Shell>
  );
}
